/* ==========================================================================
   meterService

   REAL MODE: reads/writes the `meters`, `meter_readings`, and
   `usage_history` tables (see supabase/schema.sql). listMeters() filters
   explicitly by estateContext.activeEstateId rather than relying purely
   on RLS, because a super_admin's RLS grant spans every estate — without
   an explicit filter they'd see every estate's meters mixed together
   instead of just the one currently on screen. linking and unlinking are
   both further restricted to estate_admin/super_admin
   (meters_insert_admin_only, meters_delete_admin_only), so the RBAC
   promise made elsewhere in this app (frontend hides the control, backend
   independently enforces it) holds here too.

   getReading() and getUsageHistory() read whatever is currently in
   meter_readings / usage_history — there's no real telemetry provider
   writing to those tables yet, so until one exists you'd populate them
   by hand (Supabase Table Editor) or write a small script that does.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mapMeterRow, mapReadingRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay, genReference } from "./mock/mockUtils";
import { sessionStore } from "../state/session";
import { requireActiveEstateId } from "../state/estateContext";
import type { LinkMeterPayload, Meter, MeterReading, UsageEntry } from "../types";

async function listMeters(): Promise<Meter[]> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(400);
    return mockDb.getMeters(estateId);
  }
  const { data, error } = await getSupabase()
    .from("meters")
    .select("*")
    .eq("estate_id", estateId)
    .eq("owner_id", sessionStore.user?.id ?? "")
    .order("linked_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapMeterRow);
}

async function getReading(meterId: string): Promise<MeterReading> {
  if (config.useMockServices) {
    await delay(350);
    const reading = mockDb.getReading(meterId);
    return (
      reading ?? {
        meterId,
        voltage: null,
        amps: null,
        powerFactor: null,
        unitsRemainingKwh: null,
        estimatedDaysRemaining: null,
        lastSyncAt: null,
      }
    );
  }
  const { data, error } = await getSupabase()
    .from("meter_readings")
    .select("*")
    .eq("meter_id", meterId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return mapReadingRow(meterId, data);
}

async function getUsageHistory(meterId: string, days = 7): Promise<UsageEntry[]> {
  if (config.useMockServices) {
    await delay(400);
    return mockDb.getUsageHistory(meterId).slice(-days);
  }
  const { data, error } = await getSupabase()
    .from("usage_history")
    .select("*")
    .eq("meter_id", meterId)
    .order("reading_date", { ascending: false })
    .limit(days);
  if (error) throw new Error(error.message);
  return (data ?? [])
    .map((row) => ({
      date: row.reading_date as string,
      meterId: row.meter_id as string,
      kwh: row.kwh as number,
      costKobo: row.cost_kobo as number,
      sourceType: row.source_type as "grid" | "generator",
    }))
    .reverse();
}

async function linkMeter(payload: LinkMeterPayload): Promise<Meter> {
  const estateId = requireActiveEstateId();

  if (!/^[0-9\s-]{6,}$/.test(payload.serial.trim())) {
    throw new Error("That doesn't look like a valid meter serial number.");
  }

  if (config.useMockServices) {
    await delay(800);
    const meter: Meter = {
      id: `mtr_${genReference("").toLowerCase()}`,
      serial: payload.serial.trim(),
      label: payload.label?.trim() || `Meter ${payload.serial.trim()}`,
      status: "pending_verification",
      sourceType: "grid",
      estateId,
      linkedAt: new Date().toISOString(),
    };
    mockDb.linkMeter(meter);
    return meter;
  }

  // The vending provider's meter codes are digits only (6 to 13 here). Dashes and
  // spaces are just formatting, anything else means the number is wrong.
  const serial = payload.serial.replace(/[\s-]/g, "");
  if (!/^\d{6,13}$/.test(serial)) {
    throw new Error("Meter numbers are 6 to 13 digits. Check the number and try again.");
  }

  // A resident can only claim a meter their estate admin has already registered
  // and not yet given to someone else. The database enforces this.
  const { data, error } = await getSupabase().rpc("resident_link_meter", {
    p_serial: serial,
    p_label: payload.label?.trim() || null,
  });
  if (error) throw new Error(error.message);
  const meter = mapMeterRow(data);

  // Ask the vending provider whether it knows this meter. A failure here never
  // blocks linking: the meter just stays "pending verification".
  try {
    const { data: check, error: checkErr } = await getSupabase().functions.invoke("gomelong", {
      body: { action: "verify_meter", meterId: meter.id },
    });
    if (!checkErr && check?.status === "verified") return { ...meter, status: "active", verification: "verified" };
    if (!checkErr && check?.status === "not_found") return { ...meter, verification: "not_found" };
  } catch {
    /* leave as pending verification */
  }
  return { ...meter, verification: "unknown" };
}

async function unlinkMeter(meterId: string): Promise<void> {
  if (config.useMockServices) {
    await delay(500);
    mockDb.unlinkMeter(meterId);
    return;
  }
  const { error } = await getSupabase().rpc("resident_unlink_meter", { p_meter_id: meterId });
  if (error) throw new Error(error.message);
}

export const meterService = { listMeters, getReading, getUsageHistory, linkMeter, unlinkMeter };
